import type { DialogRef } from '@angular/cdk/dialog';
import { hasModifierKey } from '@angular/cdk/keycodes';
import { Injectable, Injector } from '@angular/core';
import type { Subscription } from 'rxjs';
import { BehaviorSubject, filter, take } from 'rxjs';
import type { PalantirDialogAnimatedContainerComponent } from './components/dialog-animated-container/dialog-animated-container.component';
import { dialogLogger } from './logger.instance';

const logger = dialogLogger.extend('ref');

@Injectable()
export class PalantirDialogRef<T = any> {
  readonly #escapeKey = 'Escape';

  private dialogClosed$ = new BehaviorSubject<boolean>(false);

  private result: unknown = undefined;

  private _dialogRef?: DialogRef<any>;

  private backdropClickSubscription?: Subscription;

  private keydownEventsSubscription?: Subscription;

  private isClosing = false;

  private debugId?: string | null;

  public constructor(private readonly injector?: Injector) {}

  public setDebugId(debugId: string | null): void {
    this.debugId = debugId;
  }

  public async close(result?: unknown): Promise<void> {
    if (this.isClosing) {
      return;
    }

    this.isClosing = true;

    if (this.dialogRef?.config?.data?.canClose) {
      logger.debug(`Checking if dialog can close (DebugId: ${this.debugId})`);
      const canClose = await this.dialogRef.config?.data?.canClose();
      logger.debug(`Dialog can close (DebugId: ${this.debugId})`, canClose);
      if (!canClose) {
        logger.info(`Dialog cannot close (DebugId: ${this.debugId})`);
        this.isClosing = false;
        return;
      }
    }

    if (this.dialogRef?.config?.data?.confirmation) {
      logger.debug(`Checking if dialog can confirm (DebugId: ${this.debugId})`);
      const confirmation = this.dialogRef.config.data.confirmation;
      const componentInstance = this.dialogRef.componentInstance;

      if (confirmation.evaluate(componentInstance)) {
        let confirmed = false;

        if (confirmation.confirm) {
          confirmed = await confirmation.confirm();
        } else if (this.injector) {
          logger.debug(`Opening confirm dialog (DebugId: ${this.debugId})`);
          const { PalantirConfirmDialogService } =
            await import('../confirm-dialog/confirm-dialog.service');
          const confirmService = this.injector.get(PalantirConfirmDialogService);
          const confirmDialogRef = confirmService.openDialog({
            title: 'Alterações não salvas',
            text: 'Você possui alterações não salvas. Tem certeza que deseja fechar?',
            successText: 'Descartar alterações',
            cancelText: 'Continuar editando',
            cancelVariant: 'neutral',
            successVariant: 'negative',
            reverseButtons: true,
          });

          const result = await confirmDialogRef.afterClosed();
          logger.debug(`Confirm dialog closed (DebugId: ${this.debugId})`, result);
          confirmed = result?.role === 'success';
        }

        if (!confirmed) {
          logger.info(`Dialog cannot confirm (DebugId: ${this.debugId})`);
          this.isClosing = false;
          return;
        }
      }
    }

    const containerInstance = this.dialogRef
      ?.containerInstance as PalantirDialogAnimatedContainerComponent;

    containerInstance.animationStateChanged
      .pipe(
        filter((animationEvent) => animationEvent.phaseName === 'start'),
        take(1),
      )
      .subscribe(() => {
        logger.debug(`Dialog animation started (DebugId: ${this.debugId})`);
        this.dialogRef?.overlayRef.detachBackdrop();
      });

    containerInstance.animationStateChanged
      .pipe(
        filter(
          (animationEvent) =>
            animationEvent.phaseName === 'done' && animationEvent.toState === 'closed',
        ),
        take(1),
      )
      .subscribe(() => {
        logger.debug(`Dialog animation ended (DebugId: ${this.debugId})`);
        this.result = result;
        this.dialogRef?.close(result);
        this.dialogClosed$.next(true);
        this.isClosing = false;
      });

    containerInstance.startExitAnimation();
  }

  public async afterClosed<R = T>(): Promise<R> {
    return new Promise((resolve) => {
      const dialogClosed = this.dialogClosed$.subscribe((closed) => {
        if (!closed) {
          return;
        }

        logger.debug(`Dialog closed (DebugId: ${this.debugId})`, this.result);
        resolve(this.result as R);
        dialogClosed.unsubscribe();
      });
    });
  }

  public get dialogRef(): DialogRef<any> | undefined {
    return this._dialogRef;
  }

  public set dialogRef(dialogRef: DialogRef<any>) {
    this.backdropClickSubscription?.unsubscribe();
    this.keydownEventsSubscription?.unsubscribe();

    this._dialogRef = dialogRef;

    this.backdropClickSubscription = this._dialogRef.backdropClick.subscribe(async () => {
      await this.close();
    });

    this.keydownEventsSubscription = this._dialogRef.keydownEvents
      .pipe(
        filter((event) => event.key === this.#escapeKey && !hasModifierKey(event)),
        take(1),
      )
      .subscribe(async (event: KeyboardEvent) => {
        event.preventDefault();
        await this.close();
      });
  }
}
